import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Inbox, PlusCircle, TrendingUp, User, LogOut, Phone, Calendar, ShieldCheck, BookOpen } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import Logo from "./Logo";
import { COMPANY_PHONE, COMPANY_PHONE_TEL, CALENDLY_URL } from "../../lib/brand";

export default function AppShell({ children }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.role === "admin" || user?.role === "agent";
  const isAdmin = user?.role === "admin";

  const onSignOut = () => {
    signOut();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0" style={{ background: "#1F2A3A" }}>
        <div className="px-6 pt-6 pb-4">
          <div className="bg-white rounded-2xl p-3 inline-flex">
            <Logo compact />
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-1 mt-2">
          <NavLink to="/dashboard" data-testid="nav-dashboard" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
            <Inbox size={18} />
            <span>{isAdmin ? "All Referrals" : isManager ? "Team Referrals" : "My Referrals"}</span>
          </NavLink>
          {!isManager && (
            <NavLink to="/submit" data-testid="nav-submit" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
              <PlusCircle size={18} />
              <span>New Referral</span>
            </NavLink>
          )}
          <NavLink to="/earnings" data-testid="nav-earnings" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
            <TrendingUp size={18} />
            <span>{isManager ? "Leaderboard" : "Earnings"}</span>
          </NavLink>
          <NavLink to="/profile" data-testid="nav-profile" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
            <User size={18} />
            <span>Profile</span>
          </NavLink>

          <div className="mt-6 mb-2 px-2 text-[10px] tracking-[2px] font-extrabold" style={{ color: "rgba(255,255,255,0.4)" }}>
            QUICK ACCESS
          </div>
          <NavLink to="/lead-guide" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
            <BookOpen size={18} />
            <span>Lead Quality Guide</span>
          </NavLink>
          {isManager && (
            <NavLink to="/manage-partners" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
              <User size={18} />
              <span>{user?.role === "agent" ? "My Team" : "Manage Partners"}</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/manage-agents" className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
              <ShieldCheck size={18} />
              <span>Manage Agents</span>
            </NavLink>
          )}
        </nav>

        <div className="p-4 space-y-2">
          <a href={`tel:${COMPANY_PHONE_TEL}`} className="flex items-center gap-2 text-sm font-bold" style={{ color: "#E3F0FB" }}>
            <Phone size={14} /> {COMPANY_PHONE}
          </a>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-bold" style={{ color: "#E3F0FB" }}>
            <Calendar size={14} /> Schedule a partner call
          </a>
          <button data-testid="signout-btn" onClick={onSignOut} className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-extrabold text-white" style={{ background: "rgba(214,40,40,0.85)" }}>
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#E8E8E8", background: "#fff" }}>
          <Logo compact />
          <MobileMenu isAdmin={isAdmin} isManager={isManager} onSignOut={onSignOut} />
        </div>
        <main className="flex-1 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

function MobileMenu({ isAdmin, isManager, onSignOut }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="p-2 rounded-lg" style={{ background: "#F5F5F5" }} data-testid="mobile-menu-btn">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative ml-auto w-72 h-full p-5" style={{ background: "#1F2A3A" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-1">
              <MobileLink to="/dashboard" label={isAdmin ? "All Referrals" : isManager ? "Team Referrals" : "My Referrals"} onClick={() => setOpen(false)} />
              {!isManager && <MobileLink to="/submit" label="New Referral" onClick={() => setOpen(false)} />}
              <MobileLink to="/earnings" label={isManager ? "Leaderboard" : "Earnings"} onClick={() => setOpen(false)} />
              <MobileLink to="/profile" label="Profile" onClick={() => setOpen(false)} />
              <MobileLink to="/lead-guide" label="Lead Quality Guide" onClick={() => setOpen(false)} />
              {isManager && <MobileLink to="/manage-partners" label="Manage Partners" onClick={() => setOpen(false)} />}
              {isAdmin && <MobileLink to="/manage-agents" label="Manage Agents" onClick={() => setOpen(false)} />}
              <button onClick={() => { setOpen(false); onSignOut(); }} className="mt-3 w-full py-2.5 rounded-full text-sm font-extrabold text-white" style={{ background: "rgba(214,40,40,0.85)" }}>Sign out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MobileLink({ to, label, onClick }) {
  return (
    <NavLink to={to} onClick={onClick} className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
      {label}
    </NavLink>
  );
}
